import { clsx } from "clsx";
import { useLayoutEffect, useRef, useState } from "react";

export type SegmentedToggleOption<T> = {
  value: T;
  label: string;
  testId?: string;
};

export type SegmentedToggleProps<T> = {
  options: SegmentedToggleOption<T>[];
  selected: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  testId?: string;
};

type IndicatorRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export function SegmentedToggle<T>({
  options,
  selected,
  onChange,
  disabled,
  testId,
}: SegmentedToggleProps<T>) {
  const selectedIndex = Math.max(
    options.findIndex((option) => option.value === selected),
    0,
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicator, setIndicator] = useState<IndicatorRect | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const el = tabRefs.current[selectedIndex];
      if (el) {
        setIndicator({
          left: el.offsetLeft,
          top: el.offsetTop,
          width: el.offsetWidth,
          height: el.offsetHeight,
        });
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, [selectedIndex]);

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className="relative flex items-center gap-0.5 rounded-md border border-tertiary bg-secondary_alt p-0.5"
      role="tablist"
    >
      {indicator && (
        <div
          aria-hidden
          className="absolute rounded-[6px] border border-primary bg-primary_alt transition-all duration-200 ease-out motion-reduce:transition-none"
          style={{
            left: indicator.left,
            top: indicator.top,
            width: indicator.width,
            height: indicator.height,
          }}
        />
      )}
      {options.map((option, index) => {
        const isSelected = option.value === selected;
        return (
          <button
            key={index}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            data-testid={option.testId}
            aria-selected={isSelected}
            disabled={disabled}
            onClick={() => {
              onChange(option.value);
            }}
            className={clsx(
              "relative z-10 rounded-[6px] px-2 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors duration-200 disabled:cursor-not-allowed",
              isSelected ? "text-secondary" : "text-quaternary",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
